import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeId = 'crt-dark' | 'crt-light' | 'clean-dark' | 'clean-light' | 'high-contrast';
export type SoundEnabled = boolean;
export type HapticFeedback = boolean;
export type AutoDrawHints = boolean;
export type ShowTimer = boolean;
export type ShowPercentile = boolean;

interface SettingsStore {
  // Theme
  themeId: ThemeId;

  // Audio
  soundEnabled: SoundEnabled;
  musicVolume: number; // 0-100
  sfxVolume: number; // 0-100

  // Haptics
  hapticFeedback: HapticFeedback;

  // Gameplay
  autoDrawHints: AutoDrawHints;
  showTimer: ShowTimer;
  showPercentile: ShowPercentile;
  defaultCodeLength: number;

  // Appearance
  crtEffectsEnabled: boolean;

  // Accessibility
  highContrastMode: boolean;
  reducedMotion: boolean;
  largeText: boolean;

  // Leaderboard
  submitToLeaderboard: boolean;
  leaderboardDisplayName: string | null;

  // Actions
  setThemeId: (themeId: ThemeId) => void;
  setSoundEnabled: (enabled: SoundEnabled) => void;
  setMusicVolume: (volume: number) => void;
  setSfxVolume: (volume: number) => void;
  setHapticFeedback: (enabled: HapticFeedback) => void;
  setAutoDrawHints: (enabled: AutoDrawHints) => void;
  setShowTimer: (enabled: ShowTimer) => void;
  setShowPercentile: (enabled: ShowPercentile) => void;
  setDefaultCodeLength: (length: number) => void;
  setCrtEffectsEnabled: (enabled: boolean) => void;
  setHighContrastMode: (enabled: boolean) => void;
  setReducedMotion: (enabled: boolean) => void;
  setLargeText: (enabled: boolean) => void;
  setSubmitToLeaderboard: (enabled: boolean) => void;
  setLeaderboardDisplayName: (name: string | null) => void;
  resetSettings: () => void;
}

const defaultSettings: Omit<
  SettingsStore,
  | 'setThemeId'
  | 'setSoundEnabled'
  | 'setMusicVolume'
  | 'setSfxVolume'
  | 'setHapticFeedback'
  | 'setAutoDrawHints'
  | 'setShowTimer'
  | 'setShowPercentile'
  | 'setDefaultCodeLength'
  | 'setCrtEffectsEnabled'
  | 'setHighContrastMode'
  | 'setReducedMotion'
  | 'setLargeText'
  | 'setSubmitToLeaderboard'
  | 'setLeaderboardDisplayName'
  | 'resetSettings'
> = {
  themeId: 'crt-dark',
  soundEnabled: true,
  musicVolume: 50,
  sfxVolume: 75,
  hapticFeedback: true,
  autoDrawHints: false,
  showTimer: true,
  showPercentile: true,
  defaultCodeLength: 3,
  crtEffectsEnabled: true,
  highContrastMode: false,
  reducedMotion: false,
  largeText: false,
  submitToLeaderboard: false,
  leaderboardDisplayName: null,
};

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      ...defaultSettings,

      setThemeId: (themeId) => set({ themeId }),

      setSoundEnabled: (soundEnabled) => set({ soundEnabled }),

      setMusicVolume: (musicVolume) => set({ musicVolume }),

      setSfxVolume: (sfxVolume) => set({ sfxVolume }),

      setHapticFeedback: (hapticFeedback) => set({ hapticFeedback }),

      setAutoDrawHints: (autoDrawHints) => set({ autoDrawHints }),

      setShowTimer: (showTimer) => set({ showTimer }),

      setShowPercentile: (showPercentile) => set({ showPercentile }),

      setDefaultCodeLength: (defaultCodeLength) => set({ defaultCodeLength }),

      setCrtEffectsEnabled: (crtEffectsEnabled) => set({ crtEffectsEnabled }),

      setHighContrastMode: (highContrastMode) => {
        set((state) => ({
          highContrastMode,
          themeId: highContrastMode ? 'high-contrast' : (state.themeId === 'high-contrast' ? 'crt-dark' : state.themeId),
        }));
      },

      setReducedMotion: (reducedMotion) =>
        set((state) => ({
          reducedMotion,
          crtEffectsEnabled: reducedMotion ? false : state.crtEffectsEnabled,
        })),

      setLargeText: (largeText) => set({ largeText }),

      setSubmitToLeaderboard: (submitToLeaderboard) =>
        set({ submitToLeaderboard }),

      setLeaderboardDisplayName: (leaderboardDisplayName) =>
        set({ leaderboardDisplayName }),

      resetSettings: () => set(defaultSettings),
    }),
    {
      name: 'crackpoint-settings-store',
    },
  ),
);

// Derived selectors
export const selectEffectiveTheme = (state: SettingsStore): ThemeId => {
  return state.highContrastMode ? 'high-contrast' : state.themeId;
};

export const selectSoundVolume = (state: SettingsStore): number => {
  return state.soundEnabled ? state.sfxVolume / 100 : 0;
};
